/*
BoxCoffee is a custom component that displays the details of a coffee product in a box format. It is used in the Provenance module to show the details of a coffee record. 

The component receives a record prop and displays the name, producer/roaster, origin, product ID, batch number, SKU, bean type, processing method, roast level, certifications, package size/weight, and description. The component uses the Box, Typography, and Chip components from the Material-UI library to display the details. 

The Box component is used to create a box layout for the coffee details. The Typography component is used to display the title and description of the coffee. The Chip components are used to display the processing method, roast level, and certifications.

The component is used in the ProvenanceList component in the Provenance module to display the details of a coffee record.
*/

import React from 'react';
import { useTheme, Typography, Chip, Box } from '@mui/material';
import { TextInput, SelectInput } from 'react-admin';
import { required } from 'ra-core';

/**
 * Coffee product box component
 * @component
 * @example
 * // Example record structure:
 * const coffeeRecord = {
 *   id: "C123",
 *   name: "Ethiopian Yirgacheffe",
 *   producer: "Altitude Coffee Roasters",
 *   origin: "Ethiopia, Yirgacheffe",
 *   beanType: "100% Arabica",
 *   processingMethod: "Washed",
 *   roastLevel: "Medium",
 *   certifications: ["Organic", "Fair Trade"],
 *   packageSize: "12 oz / 340g",
 *   batchNumber: "BN789",
 *   sku: "SKU456",
 *   description: "Bright and fruity with notes of blueberry and citrus...",
 *   image: "url/to/coffee/image.jpg"
 * };
 */

export const defaultCoffeeImage = "https://placehold.co/200x300?text=Coffee&font=playfair-display";

const BoxCoffee = ({ record }) => {
    const theme = useTheme();
    return (
        <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" sx={{ color: theme.palette.primary.main, mb: 1 }}>
                {record.name || 'Unnamed Coffee'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Producer/Roaster:</strong> {record?.producer || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Origin:</strong> {record?.origin || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Bean Type:</strong> {record?.beanType || 'Not specified'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Chip 
                    label={record?.processingMethod || 'Processing N/A'} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                />
                <Chip 
                    label={record?.roastLevel || 'Roast N/A'} 
                    size="small" 
                    color="secondary" 
                    variant="outlined" 
                />
                {record?.certifications && Array.isArray(record.certifications) && 
                    record.certifications.map((cert, index) => (
                        <Chip 
                            key={index} 
                            label={cert} 
                            size="small" 
                        />
                    ))}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Package Size:</strong> {record?.packageSize || 'Not specified'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Typography variant="body2"><strong>Product ID:</strong> {record.id || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Batch Number:</strong> {record?.batchNumber || 'N/A'}</Typography>
                <Typography variant="body2"><strong>SKU:</strong> {record?.sku || 'N/A'}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <strong>Description:</strong> {record?.description || 'No description available'}
                </Typography>
            </Box>
        </Box>
    );
};

export const renderCoffeeFields = () => {
    return (
        <>
            <TextInput label="Coffee Name" source="data.name" validate={required()} />
            <TextInput label="Producer/Roaster" source="data.producer" validate={required()} />
            <TextInput label="Origin" source="data.origin" validate={required()} />
            <TextInput label="Bean Type" source="data.beanType" validate={required()} />
            <SelectInput 
                label="Processing Method" 
                source="data.processingMethod" 
                choices={[
                    { id: 'Washed', name: 'Washed' },
                    { id: 'Natural', name: 'Natural' },
                    { id: 'Honey', name: 'Honey' },
                    { id: 'Wet-hulled', name: 'Wet-hulled' },
                    { id: 'Anaerobic', name: 'Anaerobic' }
                ]} 
            />
            <SelectInput 
                label="Roast Level" 
                source="data.roastLevel" 
                choices={[
                    { id: 'Light', name: 'Light' },
                    { id: 'Medium', name: 'Medium' },
                    { id: 'Medium-Dark', name: 'Medium-Dark' },
                    { id: 'Dark', name: 'Dark' }
                ]} 
            />
            <SelectInput 
                label="Certifications" 
                source="data.certifications" 
                choices={[
                    { id: 'Organic', name: 'Organic' },
                    { id: 'Fair Trade', name: 'Fair Trade' },
                    { id: 'Rainforest Alliance', name: 'Rainforest Alliance' },
                    { id: 'Direct Trade', name: 'Direct Trade' },
                    { id: 'Bird Friendly', name: 'Bird Friendly' }
                ]}
                multiple
            />
            <TextInput label="Package Size" source="data.packageSize" validate={required()} />
            <TextInput label="Batch #" source="data.batchNumber" validate={required()} />
            <TextInput label="SKU" source="data.sku" validate={required()} />
            <TextInput label="Price" source="data.price" validate={required()} />
            <TextInput label="Description" multiline source="data.description" />
        </>
    );
};

export default BoxCoffee;